import { t } from '@lingui/core/macro';
import { ArrowUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from './ui/alert';

/**
 * Shown while a camera or location request has waited over 1.5 s for an answer.
 * When Chrome lacks its Android camera permission it asks in a thin strip at the
 * top of the screen that closes itself after a few seconds; in landscape it is
 * easy to miss. Worded so it is harmless when the wait is just a slow camera.
 */
export function BrowserPromptHint({ waiting }: { waiting: boolean }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!waiting) return;
    const timer = setTimeout(() => setShow(true), 1500);
    return () => {
      clearTimeout(timer);
      setShow(false);
    };
  }, [waiting]);

  // Only Chrome on Android asks in a strip at the top. iOS Safari's prompts are centred dialogs: no arrow up there.
  if (!show || !/Android/i.test(navigator.userAgent)) return null;

  // top-24: below Chrome's prompt (it reaches ~70px into the page), so it points at it instead of hiding under it.
  // role="status": a hint, announced politely, not an alert.
  return (
    <Alert
      role="status"
      className="fixed top-24 left-1/2 z-[80] w-auto max-w-md -translate-x-1/2 items-center border-transparent bg-black/85 text-white shadow-lg"
    >
      <ArrowUp className="size-6 animate-bounce" />
      <AlertDescription className="text-base text-white">
        {t`If the browser asks for permission, confirm it. Check the top of the screen too.`}
      </AlertDescription>
    </Alert>
  );
}
